import { IDropdownOption, MessageBarType } from '@fluentui/react';
import { useBoolean } from '@fluentui/react-hooks';
import {
  BaseDialog,
  Button,
  buttonStylesDanger,
  buttonStylesGhost,
  buttonStylesPrimary,
  Dropdown,
  hasChangedFieldValues,
  IMessageBarMessage,
  Link,
  linkStylesGhost,
  MessageBar,
  TextField,
  useClassNames,
  useToast,
} from '@h2oai/ui-kit';
import { FormEvent, useCallback, useEffect, useState } from 'react';

import { fsApi } from '../../featurestore/api';
import {
  AccessModifier,
  ActivePermission,
  CoreService,
  CreateProjectResponse,
  ListableProject,
  Project,
  UpdatableProjectField,
  UpdateProjectRequest,
} from '../../featurestore/gen/ai/h2o/featurestore/api/v1/CoreService.pb';
import { accessModifierNameMap, buttonStylesPrimaryPanel } from '../../featurestore/models';
import { getErrorMessageBarMessage } from '../../featurestore/utils';
import {
  createUpdateProjectStylesDefault,
  ICreateUpdateProjectClassNames,
  ICreateUpdateProjectStyles,
} from './CreateUpdateProject.styles';

const getCreateProjectRequest = ({
  name: projectName = '',
  description = '',
  accessModifier = AccessModifier.ACCESS_MODIFIER_PRIVATE,
}: Project) => ({ projectName, description, accessModifier });

const getEmptyProject = () => ({
  name: '',
  description: '',
  accessModifier: AccessModifier.ACCESS_MODIFIER_PRIVATE,
});

interface CreateUpdateProjectFields {
  id?: string;
  name?: string;
  description?: string;
  accessModifier?: AccessModifier;
}

const getProjectFields = ({
  id,
  name,
  description,
  accessModifier = AccessModifier.ACCESS_MODIFIER_PRIVATE,
}: Project): CreateUpdateProjectFields => ({
  id,
  name,
  description,
  accessModifier,
});

const isOwner = (permission?: ActivePermission) => permission === ActivePermission.ACTIVE_PERMISSION_OWNER;

type ProjectUpdateBag = {
  name: keyof Project;
  enumType: UpdatableProjectField;
};

interface ICreateUpdateProjectProps {
  project?: ListableProject;
  onUpdate?: (project?: Project, deleted?: boolean, message?: IMessageBarMessage) => void;
  onCancel?: () => void;
}

const getAccessModifierOptions = () => {
  const options: IDropdownOption[] = [];
  accessModifierNameMap.forEach((text, key) => {
    options.push({ key, text });
  });
  return options;
};

export const CreateUpdateProject = ({ project = getEmptyProject(), onUpdate, onCancel }: ICreateUpdateProjectProps) => {
  const { id: projectId, name: projectName, permission } = project;
  const [previousValues, setPreviousValues] = useState<CreateUpdateProjectFields>(getProjectFields(project));
  const [values, setValues] = useState<CreateUpdateProjectFields>({ ...previousValues });
  const [isReadyToDelete, setIsReadyToDelete] = useState(false);
  const { addToast } = useToast();
  const isEdit = Boolean(projectId);
  const showAccessModifierDropDown = !isEdit || isOwner(permission);
  const [hideDialog, { toggle: toggleHideDialog }] = useBoolean(true);

  const load = useCallback(async () => {
    try {
      const { project } = await fsApi(CoreService.GetProjectById, { projectId });
      const fields = getProjectFields(project);
      setPreviousValues({ ...fields });
      setValues({ ...fields });
    } catch (err) {
      addToast(getErrorMessageBarMessage(err, `An error has occurred while reloading the project: `));
    }
  }, []);
  const createProject = useCallback(async () => {
    let updatedProject: Project | undefined;
    let message: IMessageBarMessage | undefined;
    try {
      const { project: result }: CreateProjectResponse = await fsApi(
        CoreService.CreateProject,
        getCreateProjectRequest(values)
      );
      updatedProject = result;
      message = {
        messageBarType: MessageBarType.success,
        message: 'Project has been created successfully!',
      };
      addToast(message);
    } catch (err) {
      message = getErrorMessageBarMessage(err, `An error has occurred while creating your project: `);
      addToast(message);
    } finally {
      onUpdate && onUpdate(updatedProject, undefined, message);
    }
  }, [values]);

  const updateProject = useCallback(async () => {
    try {
      const { id: projectId } = values;
      const request: UpdateProjectRequest = (
        [
          { name: 'description', enumType: UpdatableProjectField.PROJECT_DESCRIPTION },
          { name: 'accessModifier', enumType: UpdatableProjectField.PROJECT_ACCESS_MODIFIER },
        ] as ProjectUpdateBag[]
      )
        .filter(
          ({ name }) =>
            previousValues[name as keyof CreateUpdateProjectFields] !== values[name as keyof CreateUpdateProjectFields]
        )
        .reduce<UpdateProjectRequest>(
          (result: UpdateProjectRequest, { name, enumType }: ProjectUpdateBag) =>
            ({
              ...result,
              [name]: values[name as keyof CreateUpdateProjectFields],
              fieldsToUpdate: [...(result.fieldsToUpdate || []), enumType],
            } as UpdateProjectRequest),
          { projectId } as UpdateProjectRequest
        );
      await fsApi(CoreService.UpdateProject, request);
      addToast({
        messageBarType: MessageBarType.success,
        message: 'Project has been updated successfully!',
      });
    } catch (err) {
      addToast(getErrorMessageBarMessage(err, `An error has occurred while saving or loading the project: `));
    } finally {
      onUpdate && onUpdate(values);
    }
  }, [previousValues, values]);

  const onSubmit = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      if (project.id) {
        updateProject();
      } else {
        createProject();
      }
      e.preventDefault();
    },
    [project, values]
  );

  const onDelete = useCallback(async () => {
    try {
      await fsApi(CoreService.DeleteProject, { projectId });
      addToast({
        messageBarType: MessageBarType.success,
        message: 'Project has been deleted successfully!',
      });
    } catch (err) {
      addToast(getErrorMessageBarMessage(err, `An error has occurred while deleting your project: `));
    } finally {
      if (onUpdate) onUpdate(undefined, true);
    }
  }, [project]);
  const classNames = useClassNames<ICreateUpdateProjectStyles, ICreateUpdateProjectClassNames>(
    'CreateUpdateProject',
    createUpdateProjectStylesDefault
  );

  useEffect(() => {
    if (projectId) load();
  }, []);

  return (
    <div className={classNames.root} data-test="create-update-project">
      <form className={classNames.form} onSubmit={onSubmit}>
        <div className={classNames.fields}>
          {!isEdit && (
            <TextField
              name="projectName"
              label="Project Name"
              required
              value={values.name}
              onChange={(_e, name) => {
                setValues({ ...values, name: name?.trimStart() });
              }}
            />
          )}
          <TextField
            name="description"
            label="Project Description"
            multiline
            rows={3}
            value={values.description}
            onChange={(_e, description) => {
              setValues({ ...values, description });
            }}
          />
          {showAccessModifierDropDown && (
            <>
              <Dropdown
                styles={{ root: { width: '120px' } }}
                label="Access Modifiler"
                placeholder="Select Access Modifier"
                selectedKey={values.accessModifier}
                options={getAccessModifierOptions()}
                onChange={(_e, option) => {
                  if (option) {
                    setValues({ ...values, accessModifier: option.key as AccessModifier });
                  }
                }}
              />
            </>
          )}
        </div>
        <div className={classNames.buttons}>
          <div>
            {isOwner(permission) && (
              <Button
                tabIndex={-1}
                styles={buttonStylesDanger}
                text="Delete"
                onClick={toggleHideDialog}
                data-test="button-delete"
              />
            )}
          </div>
          <div>
            <Button styles={buttonStylesGhost} text="Cancel" onClick={onCancel} style={{ marginRight: 8 }} />
            <Button
              styles={[buttonStylesPrimary, buttonStylesPrimaryPanel]}
              type="submit"
              text={isEdit ? 'Save' : 'Create'}
              disabled={(isEdit && !hasChangedFieldValues(previousValues, values)) || (!isEdit && !values.name?.trim())}
            />
          </div>
        </div>
      </form>
      <BaseDialog
        hidden={hideDialog}
        onDismiss={toggleHideDialog}
        title={`Delete ${projectName}`}
        data-test="dialog-delete"
        content={
          <>
            <MessageBar messageBarType={MessageBarType.error}>
              Deleting project deletes also all feature sets in that project.
              <br />
              Are you sure you want to delete <strong>{projectName}</strong>?
            </MessageBar>
            <div style={{ marginTop: 16 }}>
              <TextField
                label="Please type the project name to be deleted"
                placeholder="Project name"
                onChange={(_e, value) => setIsReadyToDelete(value === projectName)}
              />
            </div>
          </>
        }
        footer={
          <div style={{ display: 'flex', justifyContent: 'space-between' }} data-test="dialog-footer">
            <Button
              disabled={!isReadyToDelete}
              styles={buttonStylesDanger}
              text="Delete"
              onClick={() => {
                onDelete();
                toggleHideDialog();
              }}
              data-test="dialog-button-delete"
            />
            <Link styles={[linkStylesGhost, { root: { marginRight: 8 } }]} onClick={toggleHideDialog}>
              Cancel
            </Link>
          </div>
        }
      ></BaseDialog>
    </div>
  );
};
